import {
  DocumentDuplicateIcon,
  InformationCircleIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useInfo } from "../../hooks/useData";
import Button from "../Inputs/Button";
import Input from "../Inputs/Input";
import MapSection from "../MapSection";
import YearlyIncreaseComponent from "./YearlyIncrease";
import { convertToMoYr } from "../../utils";
import Layout from "../Layout";
import Confirm from "../Confirm";
import { useState } from "react";
import { CurrentSpending, NewSpending } from "src/types";
import { useUser } from "src/hooks/useUser";
import StartEndSelect from "../Info/StartEndSelect";

const SpendingPage = () => {
  const { data, setSpending, setField: set } = useInfo();
  const { user } = useUser();
  const setField = (key: string) => (val: any) => {
    setSpending({ ...spending, [key]: val });
  };

  const setPreSpending = (
    index: number,
    field: keyof CurrentSpending,
    value: CurrentSpending[typeof field],
  ) => {
    setField("preSpending")(
      spending.preSpending.map((item, i) =>
        index === i ? { ...item, [field]: value } : item,
      ),
    );
  };

  const setPostSpending = (
    index: number,
    field: keyof NewSpending,
    value: NewSpending[typeof field],
  ) => {
    setField("postSpending")(
      spending.postSpending.map((item, i) =>
        index === i ? { ...item, [field]: value } : item,
      ),
    );
  };

  const spending = data.spending || { yearlyIncrease: {} };

  const [postDeleteOpen, setPostDeleteOpen] = useState(-1);
  const [preDeleteOpen, setPreDeleteOpen] = useState(-1);

  const initialSpending = {
    preTaxRate: user?.info?.globalPreRetirementTaxRate,
    postTaxRate: user?.info?.globalPostRetirementTaxRate,
    retirementYear: undefined as any,
    preSpending: [],
    postSpending: [],
    yearlyIncrease: { type: "general" },
    newCurrentSpending: { type: "yearly", value: 0 },
    currentSpending: 0,
    decreaseAtDeath: [0, 0] as any,
  };

  const [resetOpen, setResetOpen] = useState(false);

  return (
    <Layout page="spending">
      <div className="flex flex-col gap-8 mt-[-20px]">
        <div className="flex justify-end items-center gap-4">
          <InformationCircleIcon className="h-7 w-7 text-gray-700 cursor-pointer" />
          <div className="w-40">
            <Button type="secondary" onClick={() => setResetOpen(true)}>
              Reset
            </Button>

            <Confirm
              isOpen={resetOpen}
              onClose={() => setResetOpen(false)}
              onConfirm={() => {
                set("spending")(initialSpending);
                setResetOpen(false);
              }}
            >
              <TrashIcon className="text-slate-400 w-10 m-auto mb-5" />
              <div className="mb-5">
                Are you sure you want to reset spending? This will delete all
                your spending inputs.
              </div>
            </Confirm>
          </div>
        </div>
        <MapSection
          title={<div className="py-2 px-3">Current Spending</div>}
          toggleabble
          defaultOpen
        >
          <div className="">
            <table className="">
              <thead
                className={`text-xs cursor-pointer text-left sticky z-50  font-normal!`}
              >
                <tr>
                  <th className="px-4 py-1 font-medium">
                    Amount (Today's Dollars)
                  </th>
                  <th className="px-5 py-1 font-medium">Yearly increase</th>
                  {data.people.map((v) => (
                    <th className="px-5 py-1 font-medium" key={v.id}>
                      {`Decrease at ${v.name} Death`}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="">
                  <td className="px-4 py-2 ">
                    <Input
                      label=""
                      vertical
                      size="lg"
                      value={
                        spending.newCurrentSpending
                          ? spending.newCurrentSpending
                          : convertToMoYr(spending.currentSpending)
                      }
                      setValue={(v) =>
                        setSpending({ ...spending, newCurrentSpending: v })
                      }
                      subtype="mo/yr"
                    />
                  </td>
                  <td className="px-4 py-2 ">
                    <YearlyIncreaseComponent
                      labels={false}
                      vertical={false}
                      increase={spending.yearlyIncrease}
                      setYearlyIncrease={setField("yearlyIncrease")}
                    />
                  </td>
                  {data.people.map((_, i) => (
                    <td className="px-4 py-2">
                      <Input
                        vertical
                        size="lg"
                        value={spending.decreaseAtDeath[i]}
                        setValue={(value: number) =>
                          setSpending({
                            ...spending,
                            decreaseAtDeath: spending.decreaseAtDeath.map(
                              (val, ind) => (ind == i ? value : val),
                            ) as [number, number],
                          })
                        }
                        subtype="percent"
                        label={""}
                      />
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </MapSection>
        <MapSection
          title={
            <div className="flex gap-6 items-center w-full p-2">
              <div>Current Spending That Ends </div>
              <div className="w-32">
                <Button
                  type="primary"
                  className="py-1!"
                  onClick={(e) => {
                    e.stopPropagation();

                    setField("preSpending")([
                      ...spending.preSpending,
                      {
                        increase: { type: "general" },

                        endYear: { type: "calendar-year", year: null },
                      },
                    ]);
                  }}
                >
                  <div className="flex items-center justify-center gap-2">
                    <PlusIcon className="h-4" />
                    Add
                  </div>
                </Button>
              </div>
            </div>
          }
          toggleabble
          defaultOpen
        >
          <table className="w-full">
            <thead
              className={`text-xs cursor-pointer text-left sticky z-50 font-normal!`}
            >
              <tr>
                <th className="px-3 py-3 font-medium">Category</th>
                <th className="px-3 py-3 font-medium">
                  Amount <br />
                  (Today's Dollars)
                </th>
                <th className="px-3 py-3 font-medium">Ends</th>
                <th className="px-3 py-3 font-medium">
                  Yearly <br /> Increase{" "}
                  {spending.preSpending.find(
                    (i) => i.increase.type === "custom",
                  ) && (
                      <div className="inline-block ml-16">Increase (%)</div>
                    )}{" "}
                </th>
                <th className="px-3 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {spending.preSpending.map((line, index) => (
                <tr className="">
                  <td className="px-2 py-2 w-[500px]">
                    <Input
                      vertical
                      value={line.category}
                      setValue={(v) => setPreSpending(index, "category", v)}
                      size="full"
                      subtype="text"
                      label={``}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Input
                      label=""
                      vertical
                      size="full"
                      value={
                        line.newAmount
                          ? line.newAmount
                          : { type: "yearly", value: line.amount }
                      }
                      setValue={(v) => setPreSpending(index, "newAmount", v)}
                      subtype="mo/yr"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <StartEndSelect
                      value={line.endYear}
                      setValue={(v) => setPreSpending(index, "endYear", v)}
                      shownOptions={[
                        ["calendar-year"],
                        ["#-of-years"],
                        ...(data.people.map((p) => [
                          "retirement" as const,
                          p.id,
                        ]) as any),
                        ...(data.people.map((p) => [
                          "age" as const,
                          p.id,
                        ]) as any),
                        ...(data.people.map((p) => [
                          "death" as const,
                          p.id,
                        ]) as any),
                      ]}
                      label=""
                    />
                  </td>
                  <td className="px-2 py-2">
                    <YearlyIncreaseComponent
                      labels={false}
                      increase={line.increase}
                      setYearlyIncrease={(v) =>
                        setPreSpending(index, "increase", v)
                      }
                    />
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex gap-3">
                      <Button
                        type="secondary"
                        onClick={() => {
                          setField("preSpending")(
                            spending.preSpending.flatMap((item, ind) =>
                              ind === index ? [item, { ...item }] : [item],
                            ),
                          );
                        }}
                      >
                        <div className="flex justify-center">
                          <DocumentDuplicateIcon className="h-5 text-black" />
                        </div>
                      </Button>

                      <Confirm
                        isOpen={preDeleteOpen === index}
                        onClose={() => setPreDeleteOpen(-1)}
                        onConfirm={() => {
                          setPreDeleteOpen(-1);
                          setField("preSpending")(
                            spending.preSpending.filter(
                              (_, ind) => ind !== index,
                            ),
                          );
                        }}
                      >
                        <TrashIcon className="text-slate-400 w-10 m-auto mb-5" />
                        <div className="mb-5">
                          Are you sure you want to delete this spending?
                        </div>
                      </Confirm>
                      <Button
                        type="secondary"
                        onClick={() => {
                          return setPreDeleteOpen(index);
                        }}
                      >
                        <div className="flex justify-center">
                          <TrashIcon className="h-5 text-red-500" />
                        </div>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </MapSection>
        <MapSection
          title={
            <div className="flex gap-6 items-center w-full p-2">
              <div>New Spending </div>
              <div className="w-32">
                <Button
                  type="primary"
                  className="py-1!"
                  onClick={(e) => {
                    e.stopPropagation();
                    setField("postSpending")([
                      ...spending.postSpending,
                      {
                        increase: { type: "general" },
                        changeAtDeath: data.people.map((_) => 0),
                        startYear: { type: "calendar-year", year: null },
                        endYear: { type: "calendar-year", year: null },
                      },
                    ]);
                  }}
                >
                  <div className="flex items-center justify-center gap-2">
                    <PlusIcon className="h-4" />
                    Add
                  </div>
                </Button>
              </div>
            </div>
          }
          toggleabble
          defaultOpen
        >
          <table className=" w-full bg-white">
            <thead
              className={`text-xs cursor-pointer text-left sticky z-50  text-black font-medium`}
            >
              <tr>
                <th className="px-3 py-3 font-medium">Category</th>
                <th className="px-3 py-3 font-medium">
                  Amount
                  <br /> (Today's Dollars)
                </th>
                <th className="px-3 py-3 font-medium">Starts</th>
                <th className="px-3 py-3 font-medium">Ends</th>
                <th
                  className={`px-3 py-3 font-medium ${spending.postSpending.find(
                    (i) => i.increase.type === "custom",
                  ) && "w-64"
                    }`}
                >
                  Yearly <br /> Increase{" "}
                  {spending.postSpending.find(
                    (i) => i.increase.type === "custom",
                  ) && (
                      <div className="inline-block ml-8">Increase (%)</div>
                    )}{" "}
                </th>

                {data.people.map((i) => (
                  <th className="px-6 py-3 font-medium">
                    Decrease at <br />
                    {i.name} Death
                  </th>
                ))}

                <th className="px-6 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {spending.postSpending.map((line, index) => (
                <tr className="">
                  <td className="px-2 py-2 w-[500px]">
                    <Input
                      vertical
                      value={line.category}
                      setValue={(v) => setPostSpending(index, "category", v)}
                      size="full"
                      subtype="text"
                      label={``}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Input
                      label=""
                      vertical
                      size="md"
                      value={
                        line.newAmount
                          ? line.newAmount
                          : { type: "yearly", value: line.amount }
                      }
                      setValue={(v) => setPostSpending(index, "newAmount", v)}
                      subtype="mo/yr"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <StartEndSelect
                      value={line.startYear}
                      setValue={(v) => setPostSpending(index, "startYear", v)}
                      shownOptions={[
                        ["calendar-year"],
                        ["#-of-years"],
                        ...(data.people.map((p) => [
                          "retirement" as const,
                          p.id,
                        ]) as any),
                        ...(data.people.map((p) => [
                          "age" as const,
                          p.id,
                        ]) as any),
                        ...(data.people.map((p) => [
                          "death" as const,
                          p.id,
                        ]) as any),
                      ]}
                      label=""
                    />
                  </td>

                  <td className="px-2 py-2">
                    <StartEndSelect
                      value={line.endYear}
                      setValue={(v) => setPostSpending(index, "endYear", v)}
                      shownOptions={[
                        ["calendar-year"],
                        ["#-of-years"],
                        ...(data.people.map((p) => [
                          "retirement" as const,
                          p.id,
                        ]) as any),
                        ...(data.people.map((p) => [
                          "age" as const,
                          p.id,
                        ]) as any),
                        ...(data.people.map((p) => [
                          "death" as const,
                          p.id,
                        ]) as any),
                      ]}
                      label=""
                    />
                  </td>
                  <td className="px-2 py-2">
                    <YearlyIncreaseComponent
                      labels={false}
                      increase={line.increase}
                      setYearlyIncrease={(v) =>
                        setPostSpending(index, "increase", v)
                      }
                    />
                  </td>
                  {data.people.map((_, index2) => (
                    <td className="px-2 py-2">
                      <Input
                        vertical
                        size="md"
                        width="w-32!"
                        value={line.changeAtDeath[index2]}
                        setValue={(v) =>
                          setPostSpending(
                            index,
                            "changeAtDeath",
                            line.changeAtDeath.map((i, ind) =>
                              ind == index2 ? v : i,
                            ) as [number, number],
                          )
                        }
                        subtype="percent"
                        label={``}
                      />
                    </td>
                  ))}
                  <td className="px-2 py-3 flex gap-2 items-center">
                    <Button
                      type="secondary"
                      onClick={() => {
                        setField("postSpending")(
                          spending.postSpending.flatMap((item, ind) =>
                            ind === index ? [item, { ...item }] : [item],
                          ),
                        );
                      }}
                    >
                      <div className="flex justify-center">
                        <DocumentDuplicateIcon className="h-5 text-black" />
                      </div>
                    </Button>

                    <Confirm
                      isOpen={postDeleteOpen === index}
                      onClose={() => setPostDeleteOpen(-1)}
                      onConfirm={() => {
                        setPostDeleteOpen(-1);
                        setField("postSpending")(
                          spending.postSpending.filter(
                            (_, ind) => ind !== index,
                          ),
                        );
                      }}
                    >
                      <TrashIcon className="text-slate-400 w-10 m-auto mb-5" />
                      <div className="mb-5">
                        Are you sure you want to delete this spending?
                      </div>
                    </Confirm>
                    <Button
                      type="secondary"
                      onClick={() => {
                        return setPostDeleteOpen(index);
                      }}
                    >
                      <div className="flex justify-center">
                        <TrashIcon className="h-5 text-red-500" />
                      </div>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </MapSection>
      </div>
    </Layout>
  );
};

export default SpendingPage;
